// src/components/Subscription/SubscriptionPromo.js
import React, { useState, useEffect } from 'react';
import { Box, Typography, Button, Paper, CircularProgress } from '@mui/material';
import { motion } from 'framer-motion';
import { CreditCard, CheckCircle, X, Check } from 'lucide-react';
import { HEALTH_GREEN } from '../Login/utils/mockData';
import agthealthService from '../../services/agthealthService';

// Colores de gradiente
const HEALTH_GREEN_GRADIENT = 'linear-gradient(135deg, #4CAF50 0%, #3D8B40 50%, #2E7031 100%)';
const GREEN_GRADIENT_START = '#4CAF50';
const GREEN_GRADIENT_MIDDLE = '#3D8B40';
const GREEN_GRADIENT_END = '#2E7031';

const SubscriptionPromo = ({ onClose, onSuccess }) => {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [subscriptionData, setSubscriptionData] = useState(null);
  const [hasUsedTrial, setHasUsedTrial] = useState(false);

  // Obtener estado de suscripción al cargar el componente
  useEffect(() => {
    const checkSubscriptionStatus = async () => {
      try {
        const response = await agthealthService.getSubscriptionStatus();
        if (response.success) {
          setSubscriptionData(response.data);
          setHasUsedTrial(response.data.hasUsedTrial || false);
        }
      } catch (err) {
        console.error('Error al verificar estado de suscripción:', err);
      }
    };

    checkSubscriptionStatus();
  }, []);

  const handleSubscribe = async () => {
    try {
      setIsLoading(true);
      setError('');
      
      const response = await agthealthService.createSubscriptionCheckout();
      
      if (response.success && response.data.url) {
        // Redirigir a la URL de checkout de Stripe
        window.location.href = response.data.url;
      } else {
        throw new Error('No se pudo crear la sesión de checkout');
      }
    } catch (err) {
      console.error('Error al iniciar suscripción:', err);
      setError(err.response?.data?.message || 'Error al procesar la solicitud');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.9 }}
      animate={{ opacity: 1, scale: 1 }}
      exit={{ opacity: 0, scale: 0.9 }}
      transition={{ duration: 0.3 }}
    >
      <Paper
        elevation={0}
        sx={{
          p: 4,
          borderRadius: 2,
          maxWidth: 500,
          mx: 'auto',
          position: 'relative',
          border: 'none',
          boxShadow: 'none'
        }}
      >
        {onClose && (
          <Button
            onClick={onClose}
            sx={{
              position: 'absolute',
              right: 8,
              top: 8,
              minWidth: 'auto',
              p: 1
            }}
          >
            <X size={20} />
          </Button>
        )}
        
        <Box sx={{ textAlign: 'center', mb: 3 }}>
          <Typography 
            variant="h4" 
            fontWeight="bold" 
            sx={{
              background: HEALTH_GREEN_GRADIENT,
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              backgroundClip: 'text',
              mb: 1
            }}
          >
            AGT Health Pro
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ mt: 1 }}>
            La solución completa para modernizar tu consulta médica
          </Typography>
        </Box>
        
        <Box sx={{ 
          display: 'flex', 
          flexDirection: 'column', 
          alignItems: 'center',
          mb: 3
        }}>
          <Typography 
            variant="h2" 
            fontWeight="bold" 
            sx={{
              background: HEALTH_GREEN_GRADIENT,
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              backgroundClip: 'text',
              fontSize: '3.5rem'
            }}
          >
            $10<Typography component="span" variant="h5" sx={{ 
              background: HEALTH_GREEN_GRADIENT,
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              backgroundClip: 'text'
            }}>/mes</Typography>
          </Typography>
          
          {/* Mostrar información de prueba gratuita solo si no la ha usado antes */}
          {!hasUsedTrial ? (
            <Typography variant="body2" sx={{ mt: 1, fontWeight: 500 }}>
              7 días de acceso completo
            </Typography>
          ) : (
            <Typography variant="body2" sx={{ mt: 1, fontWeight: 500, color: 'text.secondary' }}>
              Suscripción mensual
            </Typography>
          )}
          
          {/* Si la suscripción existe pero está en estado past_due, mostrar mensaje */}
          {subscriptionData?.status === 'past_due' && (
            <Typography variant="body2" sx={{ mt: 1, color: 'error.main', fontWeight: 500 }}>
              Tu pago anterior no pudo ser procesado. Por favor actualiza tu método de pago.
            </Typography>
          )}
        </Box>
        
        <Box sx={{ mb: 4 }}>
          {[
            'Pacientes ilimitados. Sin límites para tu práctica',
            'Gestiona pacientes y citas desde un chat con IA. Así de simple',
            'Recordatorios automáticos por WhatsApp. Sin esfuerzo, sin olvidos',
            'Tu agenda, tu perfil público. Compártelo con tus pacientes',
            'Consultas como tú las das: presenciales, virtuales o a domicilio',
            'Lo que cobras es tuyo. Sin comisiones, sin sorpresas',
            'Habla con personas, no con bots. Soporte técnico. Siempre disponible'
          ].map((feature, index) => (
            <Box key={index} sx={{ display: 'flex', alignItems: 'flex-start', mb: 1.5 }}>
              <Check 
                size={18} 
                color={GREEN_GRADIENT_START} 
                style={{ 
                  marginRight: 12, 
                  marginTop: 2,
                  flexShrink: 0
                }} 
              />
              <Typography variant="body1" sx={{ lineHeight: 1.5 }}>
                {feature}
              </Typography>
            </Box>
          ))}
        </Box>
        
        {error && (
          <Typography color="error" sx={{ mb: 2, textAlign: 'center' }}>
            {error}
          </Typography>
        )}
        
        <Button
          variant="contained"
          fullWidth
          size="large"
          disabled={isLoading}
          onClick={handleSubscribe}
          startIcon={isLoading ? null : <Check size={20} />}
          sx={{
            background: HEALTH_GREEN_GRADIENT,
            '&:hover': {
              background: `linear-gradient(135deg, ${GREEN_GRADIENT_MIDDLE} 0%, ${GREEN_GRADIENT_END} 50%, #1e5a22 100%)`,
              transform: 'translateY(-1px)',
              boxShadow: '0 8px 20px rgba(46, 112, 49, 0.3)'
            },
            py: 1.5,
            fontWeight: 'bold',
            fontSize: '1.1rem',
            transition: 'all 0.3s ease',
            boxShadow: '0 4px 15px rgba(46, 112, 49, 0.2)'
          }}
        >
          {isLoading ? (
            <CircularProgress size={24} color="inherit" />
          ) : hasUsedTrial ? (
            'Activar AGT Health Pro' 
          ) : (
            'Comenzar Prueba Gratuita'
          )}
        </Button>
        
        <Typography variant="caption" color="text.secondary" sx={{ mt: 2, display: 'block', textAlign: 'center' }}>
          {!hasUsedTrial ? (
            "Cancela cuando quieras. El cobro automático inicia tras el período de prueba."
          ) : (
            "Gestiona tu suscripción desde tu panel de control."
          )}
        </Typography>
      </Paper>
    </motion.div>
  );
};

export default SubscriptionPromo;